import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  Grid,
  Alert,
  LinearProgress,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  useTheme,
  alpha,
  Switch,
  Tooltip,
} from '@mui/material';
import {
  Search,
  Refresh,
  Edit,
  Delete,
  Visibility,
  VisibilityOff,
  Publish,
  Unpublished,
  Add,
  Clear,
  Image as ImageIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import {
  getProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  publishProduct,
  unpublishProduct,
  Product,
} from '../../../services/request/Product';
import { PaginationConst } from '../../../services/constants/AppConstant';
import { PageHeader, DataToolbar, EmptyState } from '../../../shared-components/ui';

const ProductManagement: React.FC = () => {
  const theme = useTheme();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(PaginationConst.PAGE_SIZE);
  
  // Filter state
  const [filters, setFilters] = useState({
    name: '',
    category: '',
    status: '',
    isPublished: '',
  });
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [formData, setFormData] = useState<Partial<Product>>({
    name: '',
    description: '',
    category: 'SAAS',
    price: undefined,
    currency: 'INR',
    imageUrl: '',
    subscriptionLink: '',
    features: '',
    tags: '',
  });

  const CATEGORIES = [
    { value: 'SAAS', label: 'SAAS' },
    { value: 'CLOTHES', label: 'Clothes' },
    { value: 'FINANCIAL_PRODUCTS', label: 'Financial Products' },
    { value: 'CONSULTING_SERVICES', label: 'Consulting Services' },
  ];

  const loadProducts = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const filterObj: any = {};
      if (filters.name) filterObj['name.contains'] = filters.name;
      if (filters.category) filterObj['category.equals'] = filters.category;
      if (filters.status) filterObj['status.equals'] = filters.status;
      if (filters.isPublished !== '') filterObj['isPublished.equals'] = filters.isPublished === 'true';
      
      const response = await getProducts(page, rowsPerPage, filterObj);
      console.log('Products API Response:', response); // Debug log
      if (response) {
        // Response structure from API: { data: { result: [...], totalCount: number }, message, statusCode }
        // HttpClient returns the full response, so we access response.data
        const data = response.data;
        if (data && (data.result || data.products)) {
          setProducts(data.result || data.products || []);
          setTotalCount(data.totalCount || data.count || 0);
        } else if (Array.isArray(data)) {
          // If data is directly an array
          setProducts(data);
          setTotalCount(data.length);
        } else if (response.result || response.products) {
          // Fallback: if result is at top level
          setProducts(response.result || response.products || []);
          setTotalCount(response.totalCount || response.count || 0);
        }
      }
    } catch (err: any) {
      console.error('Error loading products:', err);
      setError('Failed to load products');
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenDialog = (product?: Product) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        name: product.name || '',
        description: product.description || '',
        category: product.category || 'SAAS',
        price: product.price,
        currency: product.currency || 'INR',
        imageUrl: product.imageUrl || '',
        subscriptionLink: product.subscriptionLink || '',
        features: product.features || '',
        tags: product.tags || '',
      });
    } else {
      setEditingProduct(null);
      setFormData({
        name: '',
        description: '',
        category: 'SAAS',
        price: undefined,
        currency: 'INR',
        imageUrl: '',
        subscriptionLink: '',
        features: '',
        tags: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingProduct(null);
  };

  const handleSave = async () => {
    try {
      if (editingProduct) {
        await updateProduct(editingProduct.id, formData);
        toast.success('Product updated successfully');
      } else {
        await createProduct(formData);
        toast.success('Product created successfully');
      }
      handleCloseDialog();
      loadProducts();
    } catch (err: any) {
      console.error('Error saving product:', err);
      toast.error(err.response?.data?.message || 'Failed to save product');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }
    try {
      await deleteProduct(id);
      toast.success('Product deleted successfully');
      loadProducts();
    } catch (err: any) {
      console.error('Error deleting product:', err);
      toast.error('Failed to delete product');
    }
  };

  const handlePublish = async (id: string) => {
    try {
      await publishProduct(id);
      toast.success('Product published successfully');
      loadProducts();
    } catch (err: any) {
      console.error('Error publishing product:', err);
      toast.error('Failed to publish product');
    }
  };

  const handleUnpublish = async (id: string) => {
    try {
      await unpublishProduct(id);
      toast.success('Product unpublished successfully');
      loadProducts();
    } catch (err: any) {
      console.error('Error unpublishing product:', err);
      toast.error('Failed to unpublish product');
    }
  };

  const handleHide = async (id: string) => {
    try {
      await updateProduct(id, { status: 'INACTIVE' });
      toast.success('Product hidden successfully');
      loadProducts();
    } catch (err: any) {
      console.error('Error hiding product:', err);
      toast.error('Failed to hide product');
    }
  };

  const formatCurrency = (amount?: number, currency?: string) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency || 'INR',
    }).format(amount);
  };

  return (
    <Box>
      <PageHeader
        eyebrow="Lending · Ops"
        title="Product management"
        subtitle="Configure lending products, categories, and availability."
        actions={
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Add product
          </Button>
        }
      />

      <Card sx={{ mb: 3, p: 2 }}>
        <DataToolbar
          end={
            <Button variant="outlined" startIcon={<Refresh />} onClick={loadProducts} disabled={loading}>
              Refresh
            </Button>
          }
        >
          <TextField
            size="small"
            placeholder="Search by name..."
            value={filters.name}
            onChange={(e) => setFilters({ ...filters, name: e.target.value })}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: filters.name && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setFilters({ ...filters, name: '' })}>
                    <Clear />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: { xs: '100%', sm: 220 } }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={filters.category}
              label="Category"
              onChange={(e) => setFilters({ ...filters, category: e.target.value })}
            >
              <MenuItem value="">All</MenuItem>
              {CATEGORIES.map((cat) => (
                <MenuItem key={cat.value} value={cat.value}>
                  {cat.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filters.status}
              label="Status"
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="INACTIVE">Inactive</MenuItem>
              <MenuItem value="DELETED">Deleted</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Published</InputLabel>
            <Select
              value={filters.isPublished}
              label="Published"
              onChange={(e) => setFilters({ ...filters, isPublished: e.target.value })}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Published</MenuItem>
              <MenuItem value="false">Unpublished</MenuItem>
            </Select>
          </FormControl>
        </DataToolbar>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <Card>
        {loading && <LinearProgress />}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ background: alpha(theme.palette.primary.main, 0.1) }}>
                <TableCell sx={{ fontWeight: 700 }}>Image</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Price</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Published</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {products.length === 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <EmptyState title="No products found" />
                  </TableCell>
                </TableRow>
              ) : (
                products.map((product) => (
                  <TableRow key={product.id} hover>
                    <TableCell>
                      {product.imageUrl ? (
                        <Box
                          component="img"
                          src={product.imageUrl}
                          alt={product.name}
                          sx={{
                            width: 50,
                            height: 50,
                            objectFit: 'cover',
                            borderRadius: 1,
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            width: 50,
                            height: 50,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            borderRadius: 1,
                          }}
                        >
                          <ImageIcon color="disabled" />
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {product.name}
                      </Typography>
                      {product.description && (
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 200, display: 'block' }}>
                          {product.description.substring(0, 50)}...
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={product.category}
                        size="small"
                        color={
                          product.category === 'SAAS'
                            ? 'primary'
                            : product.category === 'CLOTHES'
                            ? 'secondary'
                            : product.category === 'FINANCIAL_PRODUCTS'
                            ? 'success'
                            : 'warning'
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {formatCurrency(product.price, product.currency)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={product.status || 'ACTIVE'}
                        size="small"
                        color={product.status === 'ACTIVE' ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={product.isPublished ? 'Published' : 'Unpublished'}
                        size="small"
                        color={product.isPublished ? 'success' : 'default'}
                        icon={product.isPublished ? <Visibility /> : <VisibilityOff />}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleOpenDialog(product)}>
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        {product.isPublished ? (
                          <Tooltip title="Unpublish">
                            <IconButton size="small" onClick={() => handleUnpublish(product.id)}>
                              <Unpublished />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Publish">
                            <IconButton size="small" onClick={() => handlePublish(product.id)}>
                              <Publish />
                            </IconButton>
                          </Tooltip>
                        )}
                        {product.status === 'ACTIVE' && (
                          <Tooltip title="Hide">
                            <IconButton size="small" onClick={() => handleHide(product.id)}>
                              <VisibilityOff />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => handleDelete(product.id)}>
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingProduct ? 'Edit Product' : 'Create New Product'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Product Name"
              fullWidth
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={formData.category}
                    label="Category"
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  >
                    {CATEGORIES.map((cat) => (
                      <MenuItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Currency</InputLabel>
                  <Select
                    value={formData.currency}
                    label="Currency"
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  >
                    <MenuItem value="INR">INR</MenuItem>
                    <MenuItem value="USD">USD</MenuItem>
                    <MenuItem value="EUR">EUR</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <TextField
              label="Price"
              fullWidth
              type="number"
              value={formData.price || ''}
              onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || undefined })}
            />
            <TextField
              label="Image URL"
              fullWidth
              value={formData.imageUrl}
              onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })}
              placeholder="https://example.com/image.jpg"
            />
            {formData.category === 'SAAS' && (
              <TextField
                label="Subscription Link"
                fullWidth
                value={formData.subscriptionLink}
                onChange={(e) => setFormData({ ...formData, subscriptionLink: e.target.value })}
                placeholder="https://example.com/subscribe"
              />
            )}
            <TextField
              label="Features (comma-separated)"
              fullWidth
              value={formData.features}
              onChange={(e) => setFormData({ ...formData, features: e.target.value })}
              placeholder="Feature 1, Feature 2, Feature 3"
            />
            <TextField
              label="Tags (comma-separated)"
              fullWidth
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              placeholder="tag1, tag2, tag3"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={!formData.name || !formData.category}>
            {editingProduct ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductManagement;
